'use client'

// libraries
import clsx from 'clsx'
import { useState, useEffect, useCallback, useRef } from 'react'

// components
import Section from '/react4xp/components/Section'
import RoundedBox from '/react4xp/components/RoundedBox'
import Input from '/react4xp/components/Form/Input'
import Portal from '/react4xp/components/Portal'

// svg
import { ChevronDown, Close, CloseLight, Loading } from '/react4xp/components/Icons/Ux'

// css
import './index.scss'

// animations
import { useResultsAnimation } from '/react4xp/components/Animations/useResultsAnimation'

// types
interface SearchGeneralProps {
    className?: string
    data?: {
        href?: string
        title?: string
        tags?: string[]
        intro?: string
    }[] | string
    searchBox?: {
        title?: string
        searchBar?: {
            placeholder?: string
            cta?: string
        }
        ctaText?: string
        ctas?: {
            label?: string
            url?: string
            external?: boolean
        }[]
    }
    howManyPerPage?: number
    totalItems?: number
    singleStrings?: {
        showing?: string
        results?: string
        filterTitle?: string
        filterCtaMobile?: {
            filter?: string
            filtering?: string
            activeFilters?: string
            apply?: string
        }
        loadMore?: string
        loading?: string
        clearSearch?: string
    }
    filters?: {
        id?: string
        label?: string
    }[]
    endpoint?: string
    initialValues?: {
        search?: string
        types?: string[]
    }
}

export default function SearchGeneral({
    className,
    data,
    searchBox,
    howManyPerPage = 10,
    totalItems,
    singleStrings,
    filters = [
        {
            id: 'sider',
            label: 'Sider'
        },
        {
            id: 'nyheter',
            label: 'Nyheter'
        }
    ],
    endpoint,
    initialValues
}: SearchGeneralProps) {

    // state for mobile filters modal
    const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)

    // mobile filters handlers
    const openMobileFilters = () => {
        setIsMobileFiltersOpen(true)

        setTimeout(() => {
            document.querySelector('#portal')?.classList.add('is-open')
        }, 100)
    }

    const closeMobileFilters = () => {
        document.querySelector('#portal')?.classList.remove('is-open')
        setTimeout(() => {
            setIsMobileFiltersOpen(false)
        }, 400)
    }

    // handle escape key to close mobile filters
    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isMobileFiltersOpen) {
                closeMobileFilters()
            }
        }

        if (isMobileFiltersOpen) {
            document.addEventListener('keydown', handleEscapeKey)
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden'
        }

        return () => {
            document.removeEventListener('keydown', handleEscapeKey)
            document.body.style.overflow = 'unset'
        }
    }, [isMobileFiltersOpen])

    const isActiveOption = (option: any) => {
        return selectedTypes.some((type: any) => type.id === option.id)
    }

    // state for expanded sidebar items - expanded by default
    const [expandedItems, setExpandedItems] = useState<string[]>(['filters'])

    // toggle sidebar item expansion
    const toggleSidebarItem = (itemKey: string) => {
        setExpandedItems(prev => 
            prev.includes(itemKey)
                ? prev.filter(key => key !== itemKey)
                : [...prev, itemKey]
        )
    }

    // Search logic
    const [items, setItems]: any = useState(data || [])
    const [hasMoreItems, setHasMoreItems] = useState(false)
    const [inputValue, setInputValue] = useState(initialValues?.search || '')
    const [searchTerm, setSearchTerm] = useState(initialValues?.search || '')
    const [selectedTypes, setSelectedTypes] = useState(initialValues?.types?.map((type: any) => filters?.find((filter: any) => filter.id === type)) || [])
    const [page, setPage] = useState(1)
    const [loadMore, setLoadMore] = useState(false)
    const [totalItemsState, setTotalItemsState] = useState(totalItems || 0)
    
    // ref to track load more state without causing useEffect re-runs
    const isLoadMoreRef = useRef(false)

    // animation hook - triggers on items changes (for search results and load more)
    const { containerRef: resultsRef, animateFilterChange } = useResultsAnimation([items])
    
    // Use the proper animation hook for filter/search changes
    const triggerFilterAnimation = (callback: () => void) => {
        animateFilterChange(callback)
    }

    const updateCurrentUrl = useCallback(() => {
        const origin = window.location.origin;
        const updatedUrl = new URL(`${origin}${window.location.pathname}`);
        const queryParams = new URLSearchParams();

        if (searchTerm) queryParams.append('search', searchTerm);
        if (selectedTypes.length) {
            selectedTypes.forEach((type: any) => {
                queryParams.append('types', type.id);
            });
        }

        updatedUrl.search = queryParams.toString();
        window.history.pushState({}, '', updatedUrl);
    }, [searchTerm, selectedTypes])

    const handleSearch = useCallback(async () => {
        if (!endpoint) return;
        
        const isLoadMore = isLoadMoreRef.current

        const url = new URL(endpoint);
        const queryParams = new URLSearchParams();

        queryParams.append('page', page.toString())
        queryParams.append('pageSize', howManyPerPage.toString())
        if (searchTerm) queryParams.append('search', searchTerm);
        if (selectedTypes.length > 0) {
            selectedTypes.forEach((type: any) => {
                queryParams.append('types', type.id)
            })
        }

        url.search = queryParams.toString();

        updateCurrentUrl()

        try {
            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`Network response was not ok: ${response.statusText}`);
            }

            const data = await response.json();

            if (isLoadMore) {
                setItems((prevItems: any) => [...prevItems, ...data.hits])
                isLoadMoreRef.current = false // Reset load more ref
                setLoadMore(false) // Reset loadMore state after successful append
            } else {
                setItems(data.hits)
            }

            setTotalItemsState(data.total)
            setHasMoreItems(data.hasNextPage)
        } catch(error) {
            console.error('There was a problem with the fetch operation:', error);
        }
    }, [endpoint, page, howManyPerPage, searchTerm, selectedTypes, updateCurrentUrl])

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search)
        const query = searchParams.get('search')

        if (query) {
            setSearchTerm(query)
        }
    }, [])

    useEffect(() => {
        handleSearch()
    }, [selectedTypes, page, searchTerm, handleSearch])

    const handleLoadMoreItems = () => {
        if (hasMoreItems) {
            isLoadMoreRef.current = true
            setLoadMore(true)
            setPage(page + 1)
        }
    }

    const handleSelectTypeFilter = (type: any) => {
        triggerFilterAnimation(() => {
            isLoadMoreRef.current = false // Reset load more ref for new filter
            setPage(1)
            setSelectedTypes((prev: any) => {
                const typeExists = prev.some((item: any) => item.id === type.id);
                if (typeExists) {
                    return prev.filter((item: any) => item.id !== type.id);
                }

                return [...prev, { id: type.id, label: type.label }];
            })
        })
    }

    const handleSearchTerm = () => {
        // Check if search query actually changed
        const trimmedInput = inputValue.trim()
        const trimmedCurrentQuery = searchTerm.trim()
        
        if (trimmedInput === trimmedCurrentQuery) {
            // Same search term - just scroll to results if on mobile, no animation needed
            if (window.innerWidth < 992) {
                setTimeout(() => {
                    const searchResults = document.getElementById('search-results')
                    if (searchResults) {
                        searchResults.scrollIntoView({ 
                            behavior: 'smooth',
                            block: 'start'
                        })
                    }
                }, 100)
            }
            return
        }
        
        triggerFilterAnimation(() => {
            isLoadMoreRef.current = false // Reset load more ref for new search
            setPage(1)
            setSearchTerm(inputValue)
        })
        
        // scroll to search results on mobile
        if (window.innerWidth < 992) {
            setTimeout(() => {
                const searchResults = document.getElementById('search-results')
                if (searchResults) {
                    searchResults.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'start'
                    })
                }
            }, 100)
        }
    }



    const handleClearSearch = () => {
        triggerFilterAnimation(() => {
            isLoadMoreRef.current = false // Reset load more ref for clear search
            setInputValue('')
            setSearchTerm('')
            setSelectedTypes([])
            setPage(1)
            
            // clear url params
            const origin = window.location.origin;
            const clearedUrl = new URL(`${origin}${window.location.pathname}`);
            window.history.pushState({}, '', clearedUrl);
        })
    }

    // render sidebar content
    const renderSidebarContent = () => (
        <div className={clsx(
            'search-sidebar-general__item',
            { active: expandedItems.includes('filters') }
        )}>

            <button
                className='search-sidebar-general__item__button'
                type='button'
                onClick={() => toggleSidebarItem('filters')}
            >

                <ChevronDown />

                <span className='h5'>
                    {singleStrings?.filterTitle}
                </span>

            </button>

            <div className='search-sidebar-general__item__content'>
                <div>
                    <div className='one-per-row'>

                        {filters?.map((option: any, i: any) => (
                            <button
                                key={i}
                                className={clsx(
                                    'body-small',
                                    isActiveOption(option) && 'active'
                                )}
                                type='button'
                                onClick={() => { handleSelectTypeFilter(option) }}
                            >

                                {option.label}

                                {isActiveOption(option) && (
                                    <Close />
                                )}

                            </button>
                        ))}

                    </div>
                </div>
            </div>

        </div>
    )
    
    return (
        <div className={clsx('page-search-general', className)}>

            <Section
                className='page-search-general__search-box'
                removeMarginTop
            >
                <div className='container'>
                    <RoundedBox bgColor='green-light'>
                        <div className='row'>
                            <div className='col-lg-10 offset-lg-1 col-xl-8 offset-xl-2'>

                                {searchBox?.title && (
                                    <h1 className='h1'>
                                        {searchBox.title}
                                    </h1>
                                )}

                                {searchBox?.searchBar && (
                                    <div
                                        className='page-search-general__search-box__search-bar'
                                    >

                                        <div className='input-wrapper'>

                                            <Input
                                                placeholder={searchBox.searchBar.placeholder}
                                                value={inputValue}
                                                size='large'
                                                onChange={(e) => setInputValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleSearchTerm()
                                                    }
                                                }}
                                            />

                                            {(inputValue || searchTerm) && (
                                                <button
                                                    className='clear'
                                                    type='button'
                                                    aria-label={singleStrings?.clearSearch}
                                                    onClick={handleClearSearch}
                                                >
                                                    <Close />
                                                </button>
                                            )}

                                        </div>

                                        <button
                                            className='button-black'
                                            onClick={handleSearchTerm}
                                        >
                                            <span className='button-black__text'>
                                                {searchBox.searchBar.cta}
                                            </span>
                                        </button>

                                    </div>
                                )}

                                {searchBox?.ctaText && searchBox?.ctas && (searchBox?.ctas?.length > 0) && (
                                    <div className='page-search-general__search-box__bottom'>
                                        
                                        <p className='h5'>
                                            {searchBox.ctaText}
                                        </p>

                                        <div className='page-search-general__search-box__ctas'>
                                            {searchBox?.ctas?.map((item: any, i: any) => (
                                                <a
                                                    key={i}
                                                    className='button-hollow-black'
                                                    href={item.url || '#'}
                                                    target={item.external ? '_blank' : undefined}
                                                >
                                                    {item.label}
                                                </a>
                                            ))}
                                        </div>

                                    </div>
                                )}
                                    
                            </div>
                        </div>
                    </RoundedBox>
                </div>
            </Section>

            <Section
                className='page-search-general__main'
                removeMarginTop
            >
                <div className='container'>
                    
                    <div className='row'>

                        <div className='col-lg-4 search-sidebar-general'>
                            <div className='search-sidebar-general__wrapper'>
                                {renderSidebarContent()}
                            </div>
                        </div>

                        <div className='desk-col' />

                        <div className='col-lg-8 results'>

                            <button
                                className='button-black open-mobile-filters'
                                type='button'
                                onClick={openMobileFilters}
                            >
                                {singleStrings?.filterCtaMobile?.filter}
                            </button>

                            <p
                                className='h5 results__total'
                                id='search-results'
                            >
                                {singleStrings?.showing} {totalItemsState} {singleStrings?.results}
                            </p>

                            <div className='results__list' ref={resultsRef}>
                                
                                {Array.isArray(items) && items.map((item: any, i: any) => (
                                    <a
                                        href={item.href}
                                        key={i}
                                        className='results__list__item'
                                    >
                                        
                                        <span className='h4'>
                                            {item.title}
                                        </span>

                                        <div className='results__list__item__tags'>
                                            {item.tags?.map((tag: any, i: any) => (
                                                <span
                                                    key={i}
                                                    className='body-small'
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>

                                        <div
                                            className='body-small results__list__item__content'
                                            dangerouslySetInnerHTML={{ __html: item.intro || '' }}
                                            suppressHydrationWarning
                                        />

                                    </a>
                                ))}

                                {hasMoreItems && (
                                    <div className='results__list__load-more'>
                                        
                                        <button
                                            className='button-hollow-black'
                                            type='button'
                                            onClick={handleLoadMoreItems}
                                            disabled={loadMore}
                                        >

                                            <span className='button-hollow-black__text'>
                                                {singleStrings?.loadMore}
                                            </span>

                                            {loadMore && (
                                                <span className='button-hollow-black__loading'>
                                                    <Loading />
                                                </span>
                                            )}

                                        </button>

                                    </div>
                                )}

                            </div>

                        </div>
                        
                    </div>

                </div>
            </Section>

            {isMobileFiltersOpen && (
                <Portal>
                    <div 
                        className='search-filters'
                        onClick={closeMobileFilters}
                    >
                        <div 
                            className='search-filters__modal'
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className='container'>

                                <div className='search-filters__header'>
                                    <button
                                        className='close-button'
                                        type='button'
                                        onClick={closeMobileFilters}
                                        aria-label='Close filters'
                                    >
                                        <CloseLight />
                                    </button>
                                </div>

                                <div className='search-filters__content'>
                                    {renderSidebarContent()}
                                </div>

                                <button
                                    className='button-black search-filters__apply'
                                    type='button'
                                    onClick={closeMobileFilters}
                                >
                                    {singleStrings?.filterCtaMobile?.apply}
                                </button>

                            </div>
                        </div>
                    </div>
                </Portal>
            )}

        </div>
    )
}